import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Mail, Lock, User, Loader2, Eye, EyeOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type Mode = "login" | "signup";

const AuthDialog = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { showAuthDialog, setShowAuthDialog, user } = useAuth();
  const [mode, setMode] = useState<Mode>("login");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [fullName, setFullName] = useState("");

  const isPortfolio = location.pathname === "/case-studies";
  const isNetwork = location.pathname === "/influencer-network";

  const title = isPortfolio
    ? t("Access Portfolio", "الدخول إلى الأعمال")
    : isNetwork
    ? t("Access the Influencer Network", "الدخول إلى شبكة المؤثرين")
    : t("Sign in to Book", "سجّل دخولك للحجز");

  const description = isPortfolio
    ? t(
        "Create an account or sign in to view our portfolio and case studies.",
        "أنشئ حساباً أو سجّل دخولك لعرض أعمالنا ودراسات الحالة."
      )
    : isNetwork
    ? t(
        "Create an account or sign in, then activate your subscription to explore the network.",
        "أنشئ حساباً أو سجّل دخولك، ثم فعّل اشتراكك لاستكشاف الشبكة."
      )
    : t(
        "Create an account or sign in to book your consultation session.",
        "أنشئ حساباً أو سجّل دخولك لحجز جلستك الاستشارية."
      );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { full_name: fullName },
          },
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
      }
    } catch (err: any) {
      toast({
        title: t("Error", "خطأ"),
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={showAuthDialog} onOpenChange={(open) => {
      if (!open && isPortfolio && !user) {
        setShowAuthDialog(false);
        navigate("/");
        return;
      }
      if (!open && isNetwork && !user) {
        setShowAuthDialog(false);
        navigate("/influencers");
        return;
      }
      setShowAuthDialog(open);
    }}>
      <DialogContent className="sm:max-w-md">

        <DialogHeader>
          <DialogTitle className="text-foreground">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {/* Toggle */}
        <div className="flex rounded-lg bg-muted p-1 mb-2">
          <button
            onClick={() => setMode("login")}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
              mode === "login"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t("Sign In", "تسجيل الدخول")}
          </button>
          <button
            onClick={() => setMode("signup")}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
              mode === "signup"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t("Sign Up", "إنشاء حساب")}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <AnimatePresence mode="wait">
            {mode === "signup" && (
              <motion.div
                key="name"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Label htmlFor="dialog-fullName" className="text-foreground">
                  <User className="w-4 h-4 inline me-1" />
                  {t("Full Name", "الاسم الكامل")}
                </Label>
                <Input
                  id="dialog-fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={t("Your full name", "اسمك الكامل")}
                  required
                  className="mt-1"
                />
              </motion.div>
            )}
          </AnimatePresence>

          <div>
            <Label htmlFor="dialog-email" className="text-foreground">
              <Mail className="w-4 h-4 inline me-1" />
              {t("Email", "البريد الإلكتروني")}
            </Label>
            <Input
              id="dialog-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("your@email.com", "بريدك@الإلكتروني.com")}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="dialog-password" className="text-foreground">
              <Lock className="w-4 h-4 inline me-1" />
              {t("Password", "كلمة المرور")}
            </Label>
            <div className="relative">
              <Input
                id="dialog-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                className="mt-1 pe-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute end-3 top-1/2 -translate-y-1/2 mt-0.5 text-muted-foreground hover:text-foreground transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button type="submit" size="lg" className="w-full" disabled={loading}>
            {loading && <Loader2 className="w-4 h-4 animate-spin me-2" />}
            {mode === "login"
              ? t("Sign In", "تسجيل الدخول")
              : t("Create Account", "إنشاء حساب")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AuthDialog;
